import { useState, useEffect, useCallback } from 'react';
import { getRaces, createRace, updateRace, deleteRace, importCSV, getStats, getSubmissions, approveSubmission, rejectSubmission, deleteSubmission, getSubmissionsStats, updateSubmission } from '../lib/api';
import { RACE_TYPE_LABELS, MONTH_NAMES_PT, formatDatePT, getRaceTypeBadgeClass } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription 
} from '../components/ui/dialog';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Upload, 
  Calendar,
  MapPin,
  TrendingUp,
  Search,
  CheckCircle,
  XCircle,
  Clock,
  Inbox,
  AlertTriangle
} from 'lucide-react';

const DISTRICTS = [
  "Aveiro", "Beja", "Braga", "Bragança", "Castelo Branco",
  "Coimbra", "Évora", "Faro", "Guarda", "Leiria",
  "Lisboa", "Portalegre", "Porto", "Santarém", "Setúbal",
  "Viana do Castelo", "Vila Real", "Viseu",
  "Açores", "Madeira"
];

const RACE_TYPES = ['road', 'trail', 'walk', 'kids'];

const initialFormState = {
  name: '',
  date: '',
  location: '',
  district: 'Lisboa',
  race_type: 'road',
  distances: '',
  website: '',
  description: '',
  price: '',
  is_international: false,
  is_featured: false
};

export const AdminPage = () => {
  const [races, setRaces] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMonth, setFilterMonth] = useState(null);
  const [filterInternational, setFilterInternational] = useState(false); // false = Portugal, true = Internacional
  
  // Submissions state
  const [submissions, setSubmissions] = useState([]);
  const [submissionsStats, setSubmissionsStats] = useState(null);
  const [activeTab, setActiveTab] = useState('races'); // 'races' or 'submissions'
  const [editingSubmission, setEditingSubmission] = useState(null);
  const [showEditSubmission, setShowEditSubmission] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [pendingApprovalId, setPendingApprovalId] = useState(null);
  const [submissionFormData, setSubmissionFormData] = useState({
    name: '',
    date: '',
    location: '',
    district: '',
    race_type: 'road',
    distances: '',
    website: '',
    price: '',
    submitter_email: '',
    submitter_name: '',
    notes: ''
  });
  
  // Modal states
  const [showForm, setShowForm] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editingRace, setEditingRace] = useState(null);
  const [raceToDelete, setRaceToDelete] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState(initialFormState);
  const [formLoading, setFormLoading] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { is_international: filterInternational };
      if (filterMonth) params.month = filterMonth;
      
      const [racesData, statsData, submissionsData, subStatsData] = await Promise.all([
        getRaces(params),
        getStats(),
        getSubmissions('pending'),
        getSubmissionsStats()
      ]);
      setRaces(racesData);
      setStats(statsData);
      setSubmissions(submissionsData);
      setSubmissionsStats(subStatsData);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, [filterMonth, filterInternational]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenForm = (race = null) => {
    if (race) {
      setEditingRace(race);
      setFormData({
        ...race,
        distances: race.distances?.join(', ') || ''
      });
    } else {
      setEditingRace(null);
      setFormData(initialFormState);
    }
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingRace(null);
    setFormData(initialFormState);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    
    try {
      const data = {
        ...formData,
        distances: formData.distances.split(',').map(d => d.trim()).filter(d => d)
      };
      
      if (editingRace) {
        await updateRace(editingRace.id, data);
      } else {
        await createRace(data);
      }
      
      handleCloseForm();
      fetchData();
    } catch (err) {
      console.error('Error saving race:', err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!raceToDelete) return;
    
    try {
      await deleteRace(raceToDelete.id);
      setShowDelete(false);
      setRaceToDelete(null);
      fetchData();
    } catch (err) {
      console.error('Error deleting race:', err);
    }
  };

  const handleImport = async () => {
    if (!importFile) return;
    
    setFormLoading(true);
    try {
      const result = await importCSV(importFile);
      setImportResult(result);
      fetchData();
    } catch (err) {
      console.error('Error importing CSV:', err);
      setImportResult({ error: 'Erro ao importar ficheiro' });
    } finally {
      setFormLoading(false);
    }
  };

  const handleSeed = async () => {
    setFormLoading(true);
    try {
      await seedRaces();
      fetchData();
    } catch (err) {
      console.error('Error seeding races:', err);
    } finally {
      setFormLoading(false);
    }
  };

  const filteredRaces = races.filter(race => 
    race.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    race.location.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleApproveSubmission = async (id, force = false) => {
    try {
      const result = await approveSubmission(id, force);
      
      // Check if duplicate warning returned
      if (result.warning === 'duplicate_found') {
        setDuplicateWarning(result);
        setPendingApprovalId(id);
        return;
      }
      
      setDuplicateWarning(null);
      setPendingApprovalId(null);
      fetchData();
    } catch (err) {
      console.error('Error approving submission:', err);
    }
  };

  const handleForceApprove = async () => {
    if (pendingApprovalId) {
      await handleApproveSubmission(pendingApprovalId, true);
    }
  };

  const cancelDuplicateWarning = () => {
    setDuplicateWarning(null);
    setPendingApprovalId(null);
  };

  const handleRejectSubmission = async (id) => {
    try {
      await rejectSubmission(id);
      fetchData();
    } catch (err) {
      console.error('Error rejecting submission:', err);
    }
  };

  const handleEditSubmission = (sub) => {
    setEditingSubmission(sub);
    setSubmissionFormData({
      name: sub.name || '',
      date: sub.date || '',
      location: sub.location || '',
      district: sub.district || '',
      race_type: sub.race_type || 'road',
      distances: sub.distances?.join(', ') || '',
      website: sub.website || '',
      price: sub.price || '',
      submitter_email: sub.submitter_email || '',
      submitter_name: sub.submitter_name || '',
      notes: sub.notes || ''
    });
    setShowEditSubmission(true);
  };

  const handleSaveSubmission = async () => {
    try {
      const distances = submissionFormData.distances
        .split(',')
        .map(d => d.trim())
        .filter(d => d);
      
      await updateSubmission(editingSubmission.id, {
        ...submissionFormData,
        distances
      });
      
      setShowEditSubmission(false);
      setEditingSubmission(null);
      fetchData();
    } catch (err) {
      console.error('Error updating submission:', err);
    }
  };

  const handleApproveAfterEdit = async () => {
    try {
      // First save the changes
      const distances = submissionFormData.distances
        .split(',')
        .map(d => d.trim())
        .filter(d => d);
      
      await updateSubmission(editingSubmission.id, {
        ...submissionFormData,
        distances
      });
      
      // Then approve
      await approveSubmission(editingSubmission.id);
      
      setShowEditSubmission(false);
      setEditingSubmission(null);
      fetchData();
    } catch (err) {
      console.error('Error approving submission:', err);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="admin-page">
      {/* Header */}
      <div className="bg-slate-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <h1 
            className="text-4xl font-bold uppercase tracking-tight mb-2"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Painel de Administração
          </h1>
          <p className="text-slate-400">
            Gerir provas do calendário 2026
          </p>
          
          {/* Tabs */}
          <div className="flex gap-4 mt-6">
            <button
              onClick={() => setActiveTab('races')}
              className={`px-4 py-2 rounded-lg font-bold transition-colors ${
                activeTab === 'races' 
                  ? 'bg-white text-slate-900' 
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
            >
              <Calendar className="inline mr-2" size={16} />
              Provas ({stats?.total_races || 0})
            </button>
            <button
              onClick={() => setActiveTab('submissions')}
              className={`px-4 py-2 rounded-lg font-bold transition-colors relative ${
                activeTab === 'submissions' 
                  ? 'bg-white text-slate-900' 
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
            >
              <Inbox className="inline mr-2" size={16} />
              Submissões
              {submissionsStats?.pending > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                  {submissionsStats.pending}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Races Tab */}
        {activeTab === 'races' && (
          <>
            {/* Stats */}
            {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="stat-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
                  <Calendar className="text-red-600" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-black text-slate-900 dark:text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    {stats.total_races}
                  </p>
                  <p className="text-xs text-slate-500 uppercase font-bold">Total Provas</p>
                </div>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-100 dark:bg-slate-800 rounded-lg flex items-center justify-center">
                  <TrendingUp className="text-slate-600" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-black text-slate-900 dark:text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    {stats.by_type?.road || 0}
                  </p>
                  <p className="text-xs text-slate-500 uppercase font-bold">Estrada</p>
                </div>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                  <MapPin className="text-green-600" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-black text-slate-900 dark:text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    {stats.by_type?.trail || 0}
                  </p>
                  <p className="text-xs text-slate-500 uppercase font-bold">Trail</p>
                </div>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-center justify-center">
                  <Calendar className="text-amber-600" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-black text-slate-900 dark:text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    {stats.by_type?.kids || 0}
                  </p>
                  <p className="text-xs text-slate-500 uppercase font-bold">Kids</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Actions bar */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <Button 
            onClick={() => handleOpenForm()}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="add-race-btn"
          >
            <Plus className="mr-2" size={16} />
            Nova Prova
          </Button>
          
          <Button 
            variant="outline"
            onClick={() => setShowImport(true)}
            data-testid="import-csv-btn"
          >
            <Upload className="mr-2" size={16} />
            Importar CSV
          </Button>

          {/* Portugal/Internacional Filter */}
          <div className="flex rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
            <button
              onClick={() => setFilterInternational(false)}
              className={`px-4 py-2 text-sm font-bold transition-colors ${
                !filterInternational 
                  ? 'bg-red-600 text-white' 
                  : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-50'
              }`}
            >
              🇵🇹 Portugal
            </button>
            <button
              onClick={() => setFilterInternational(true)}
              className={`px-4 py-2 text-sm font-bold transition-colors ${
                filterInternational 
                  ? 'bg-red-600 text-white' 
                  : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-50'
              }`}
            >
              🌍 Internacional
            </button>
          </div>

          {/* Month Filter */}
          <Select
            value={filterMonth ? String(filterMonth) : 'all'}
            onValueChange={(value) => setFilterMonth(value === 'all' ? null : parseInt(value))}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Todos os meses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os meses</SelectItem>
              {MONTH_NAMES_PT.map((name, i) => (
                <SelectItem key={name} value={String(i + 1)}>{name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <Input
                type="text"
                placeholder="Pesquisar..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="admin-search-input"
              />
            </div>
          </div>
        </div>

        {/* Results info */}
        <div className="mb-4 text-sm text-slate-500">
          A mostrar {filteredRaces.length} provas {filterInternational ? 'internacionais' : 'em Portugal'}
          {filterMonth && ` de ${MONTH_NAMES_PT[filterMonth - 1]}`}
        </div>

        {/* Table */}
        <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="admin-table" data-testid="races-table">
              <thead className="bg-slate-50 dark:bg-slate-800">
                <tr>
                  <th>Data</th>
                  <th>Nome</th>
                  <th>Local</th>
                  <th>Tipo</th>
                  <th>Distâncias</th>
                  <th className="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="text-center py-8 text-slate-500">
                      A carregar...
                    </td>
                  </tr>
                ) : filteredRaces.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-8 text-slate-500">
                      Nenhuma prova encontrada
                    </td>
                  </tr>
                ) : (
                  filteredRaces.map((race) => (
                    <tr key={race.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <td className="font-medium whitespace-nowrap">
                        {formatDatePT(race.date)}
                      </td>
                      <td>
                        <div className="font-bold text-slate-900 dark:text-white flex items-center gap-2">
                          {race.is_featured && <span className="text-amber-500">⭐</span>}
                          {race.name}
                        </div>
                        {race.is_international && (
                          <span className="text-xs text-red-600 font-bold">INTERNACIONAL</span>
                        )}
                      </td>
                      <td className="text-slate-600 dark:text-slate-400">
                        {race.location}, {race.district}
                      </td>
                      <td>
                        <Badge className={getRaceTypeBadgeClass(race.race_type)}>
                          {RACE_TYPE_LABELS[race.race_type]}
                        </Badge>
                      </td>
                      <td className="text-slate-600 dark:text-slate-400">
                        {race.distances?.join(', ')}
                      </td>
                      <td className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenForm(race)}
                            data-testid={`edit-race-${race.id}`}
                          >
                            <Pencil size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setRaceToDelete(race);
                              setShowDelete(true);
                            }}
                            className="text-red-600 hover:text-red-700"
                            data-testid={`delete-race-${race.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
          </>
        )}

        {/* Submissions Tab */}
        {activeTab === 'submissions' && (
          <div>
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Submissões Pendentes ({submissions.length})
            </h2>
            
            {submissions.length === 0 ? (
              <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 p-12 text-center">
                <CheckCircle className="mx-auto mb-4 text-green-500" size={48} />
                <p className="text-slate-600 dark:text-slate-400 text-lg">
                  Não há submissões pendentes
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {submissions.map((sub) => (
                  <div 
                    key={sub.id}
                    className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 p-6"
                  >
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                            <Clock size={12} className="mr-1" />
                            Pendente
                          </Badge>
                          <Badge className={getRaceTypeBadgeClass(sub.race_type)}>
                            {RACE_TYPE_LABELS[sub.race_type]}
                          </Badge>
                        </div>
                        
                        <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                          {sub.name}
                        </h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-slate-600 dark:text-slate-400">
                          <p><strong>Data:</strong> {formatDatePT(sub.date)}</p>
                          <p><strong>Local:</strong> {sub.location}, {sub.district}</p>
                          <p><strong>Distâncias:</strong> {sub.distances?.join(', ') || 'Não especificado'}</p>
                          <p><strong>Preço:</strong> {sub.price || 'Não especificado'}</p>
                          {sub.website && (
                            <p>
                              <strong>Website:</strong>{' '}
                              <a href={sub.website} target="_blank" rel="noopener noreferrer" className="text-red-600 hover:underline">
                                {sub.website}
                              </a>
                            </p>
                          )}
                        </div>
                        
                        <div className="mt-4 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                          <p className="text-sm text-slate-500 mb-1">Submetido por:</p>
                          <p className="font-medium">{sub.submitter_name || 'Anónimo'} ({sub.submitter_email})</p>
                          {sub.notes && (
                            <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                              <strong>Notas:</strong> {sub.notes}
                            </p>
                          )}
                          <p className="text-xs text-slate-400 mt-2">
                            {new Date(sub.created_at).toLocaleString('pt-PT')}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex md:flex-col gap-2">
                        <Button
                          onClick={() => handleEditSubmission(sub)}
                          className="bg-blue-600 hover:bg-blue-700 text-white flex-1"
                        >
                          <Pencil size={16} className="mr-2" />
                          Editar
                        </Button>
                        <Button
                          onClick={() => handleApproveSubmission(sub.id)}
                          className="bg-green-600 hover:bg-green-700 text-white flex-1"
                        >
                          <CheckCircle size={16} className="mr-2" />
                          Aprovar
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleRejectSubmission(sub.id)}
                          className="text-red-600 border-red-200 hover:bg-red-50 flex-1"
                        >
                          <XCircle size={16} className="mr-2" />
                          Rejeitar
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Create/Edit Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }} className="text-2xl uppercase">
              {editingRace ? 'Editar Prova' : 'Nova Prova'}
            </DialogTitle>
            <DialogDescription>
              {editingRace ? 'Atualiza os dados da prova' : 'Adiciona uma nova prova ao calendário'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Nome *</label>
              <Input
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Meia Maratona de Lisboa"
                data-testid="form-name"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Data *</label>
                <Input
                  required
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  data-testid="form-date"
                />
              </div>
              
              <div>
                <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Tipo *</label>
                <Select
                  value={formData.race_type}
                  onValueChange={(value) => setFormData({ ...formData, race_type: value })}
                >
                  <SelectTrigger data-testid="form-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RACE_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {RACE_TYPE_LABELS[type]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Local *</label>
                <Input
                  required
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Ex: Lisboa"
                  data-testid="form-location"
                />
              </div>
              
              <div>
                <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Distrito *</label>
                <Select
                  value={formData.district}
                  onValueChange={(value) => setFormData({ ...formData, district: value })}
                >
                  <SelectTrigger data-testid="form-district">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DISTRICTS.map((district) => (
                      <SelectItem key={district} value={district}>
                        {district}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Distâncias</label>
              <Input
                value={formData.distances}
                onChange={(e) => setFormData({ ...formData, distances: e.target.value })}
                placeholder="Ex: 5km, 10km, 21km"
                data-testid="form-distances"
              />
              <p className="text-xs text-slate-500 mt-1">Separar por vírgula</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Preço</label>
                <Input
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="Ex: 25€"
                  data-testid="form-price"
                />
              </div>
              
              <div>
                <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Website</label>
                <Input
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://..."
                  data-testid="form-website"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_international"
                checked={formData.is_international}
                onChange={(e) => setFormData({ ...formData, is_international: e.target.checked })}
                className="rounded border-slate-300"
                data-testid="form-international"
              />
              <label htmlFor="is_international" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Prova Internacional
              </label>
            </div>
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_featured"
                checked={formData.is_featured || false}
                onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                className="rounded border-amber-400 text-amber-500"
                data-testid="form-featured"
              />
              <label htmlFor="is_featured" className="text-sm font-medium text-amber-600 dark:text-amber-400 flex items-center gap-1">
                ⭐ Prova em Destaque
              </label>
            </div>
            
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={handleCloseForm}>
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={formLoading}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="form-submit-btn"
              >
                {formLoading ? 'A guardar...' : editingRace ? 'Atualizar' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDelete} onOpenChange={setShowDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }} className="text-2xl uppercase">
              Eliminar Prova
            </DialogTitle>
            <DialogDescription>
              Tens a certeza que queres eliminar "{raceToDelete?.name}"?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDelete(false)}>
              Cancelar
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDelete}
              data-testid="confirm-delete-btn"
            >
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import CSV Dialog */}
      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }} className="text-2xl uppercase">
              Importar CSV
            </DialogTitle>
            <DialogDescription>
              Carrega um ficheiro CSV com as provas
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg p-8 text-center">
              <input
                type="file"
                accept=".csv"
                onChange={(e) => {
                  setImportFile(e.target.files[0]);
                  setImportResult(null);
                }}
                className="hidden"
                id="csv-upload"
                data-testid="csv-file-input"
              />
              <label 
                htmlFor="csv-upload"
                className="cursor-pointer"
              >
                <Upload className="mx-auto mb-4 text-slate-400" size={40} />
                <p className="text-slate-600 dark:text-slate-400">
                  {importFile ? importFile.name : 'Clica para selecionar ficheiro'}
                </p>
              </label>
            </div>
            
            {importResult && (
              <div className={`p-4 rounded-lg ${importResult.error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                {importResult.error || importResult.message}
                {importResult.errors?.length > 0 && (
                  <ul className="mt-2 text-sm">
                    {importResult.errors.slice(0, 5).map((err) => (
                      <li key={err}>{err}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            
            <div className="text-sm text-slate-500">
              <p className="font-bold mb-1">Colunas esperadas:</p>
              <code className="text-xs bg-slate-100 dark:bg-slate-800 p-2 rounded block">
                name, date, location, district, race_type, distances, website, price
              </code>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowImport(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleImport}
              disabled={!importFile || formLoading}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="import-submit-btn"
            >
              {formLoading ? 'A importar...' : 'Importar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Submission Dialog */}
      <Dialog open={showEditSubmission} onOpenChange={setShowEditSubmission}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }} className="text-2xl uppercase">
              Editar Submissão
            </DialogTitle>
            <DialogDescription>
              Corrige os dados antes de aprovar a prova
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Nome da Prova *</label>
              <Input
                required
                value={submissionFormData.name}
                onChange={(e) => setSubmissionFormData({ ...submissionFormData, name: e.target.value })}
                placeholder="Ex: Meia Maratona de Lisboa"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Data *</label>
                <Input
                  required
                  type="date"
                  value={submissionFormData.date}
                  onChange={(e) => setSubmissionFormData({ ...submissionFormData, date: e.target.value })}
                />
              </div>
              
              <div>
                <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Tipo *</label>
                <Select
                  value={submissionFormData.race_type}
                  onValueChange={(value) => setSubmissionFormData({ ...submissionFormData, race_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="road">Estrada</SelectItem>
                    <SelectItem value="trail">Trail</SelectItem>
                    <SelectItem value="walk">Caminhada</SelectItem>
                    <SelectItem value="kids">Kids</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Local *</label>
                <Input
                  required
                  value={submissionFormData.location}
                  onChange={(e) => setSubmissionFormData({ ...submissionFormData, location: e.target.value })}
                  placeholder="Ex: Cascais"
                />
              </div>
              
              <div>
                <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Distrito *</label>
                <Select
                  value={submissionFormData.district}
                  onValueChange={(value) => setSubmissionFormData({ ...submissionFormData, district: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {DISTRICTS.map(d => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Distâncias</label>
              <Input
                value={submissionFormData.distances}
                onChange={(e) => setSubmissionFormData({ ...submissionFormData, distances: e.target.value })}
                placeholder="Ex: 5km, 10km, 21km (separar por vírgula)"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Website</label>
                <Input
                  type="url"
                  value={submissionFormData.website}
                  onChange={(e) => setSubmissionFormData({ ...submissionFormData, website: e.target.value })}
                  placeholder="https://..."
                />
              </div>
              
              <div>
                <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Preço</label>
                <Input
                  value={submissionFormData.price}
                  onChange={(e) => setSubmissionFormData({ ...submissionFormData, price: e.target.value })}
                  placeholder="Ex: 15€"
                />
              </div>
            </div>
            
            <div className="border-t pt-4 mt-4">
              <p className="text-sm text-slate-500 mb-2">Submetido por: <strong>{editingSubmission?.submitter_name || 'Anónimo'}</strong> ({editingSubmission?.submitter_email})</p>
              {editingSubmission?.notes && (
                <p className="text-sm text-slate-500">Notas: {editingSubmission.notes}</p>
              )}
            </div>
          </div>
          
          <DialogFooter className="gap-2 flex-col sm:flex-row">
            <Button type="button" variant="outline" onClick={() => setShowEditSubmission(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSaveSubmission}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Guardar Alterações
            </Button>
            <Button 
              onClick={handleApproveAfterEdit}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <CheckCircle size={16} className="mr-2" />
              Guardar e Aprovar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Warning Dialog */}
      <Dialog open={!!duplicateWarning} onOpenChange={() => cancelDuplicateWarning()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle size={24} />
              Possível Duplicado Detetado
            </DialogTitle>
            <DialogDescription>
              {duplicateWarning?.message}
            </DialogDescription>
          </DialogHeader>
          
          {duplicateWarning?.existing_race && (
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
              <p className="font-bold text-slate-900 dark:text-white">{duplicateWarning.existing_race.name}</p>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {duplicateWarning.existing_race.date} - {duplicateWarning.existing_race.location}
              </p>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={cancelDuplicateWarning}>
              Cancelar
            </Button>
            <Button 
              onClick={handleForceApprove}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              Aprovar Mesmo Assim
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
